'use client';

import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

export default function TermsPage() {
  const lastUpdated = 'June 1, 2026';

  const sections = [
    {
      title: '1. Acceptance of Terms',
      text: 'By accessing or using the Lendo platform ("Service"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, please do not use our Service. These Terms apply to all users, including visitors, registered users, and business applicants.',
    },
    {
      title: '2. Description of Service',
      text: 'Lendo is a loan matching platform that connects small business owners and individuals with a network of lending partners. We do not directly provide loans, credit, or financing. We facilitate introductions between borrowers and lenders. Lendo is not a lender, broker, or financial advisor.',
    },
    {
      title: '3. Eligibility',
      text: 'To use our Service, you must be at least 18 years of age, a legal resident or citizen of the United States, have a valid Social Security Number or EIN, and have the legal authority to enter into binding contracts. By using our Service, you represent and warrant that you meet all eligibility requirements.',
    },
    {
      title: '4. Account Registration',
      text: 'To access certain features, you must create an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. Notify us immediately of any unauthorized use of your account.',
    },
    {
      title: '5. Loan Application Process',
      text: 'When you submit a loan application through Lendo, you authorize us to share your information with up to 5 lending partners in our network. Submitting an application does not guarantee loan approval or any specific loan terms. Lenders make independent credit decisions. Any loan offers you receive are from third-party lenders, not from Lendo. Review all loan terms carefully before accepting any offer.',
    },
    {
      title: '6. Soft Credit Pull',
      text: 'Lendo performs only a soft credit inquiry when processing your initial application, which does not affect your credit score. However, if a lender decides to proceed with your application, they may perform a hard credit inquiry, which may impact your credit score. You will be notified before any hard inquiry is made.',
    },
    {
      title: '7. Fees',
      text: 'Lendo does not charge borrowers any fees for using our loan matching service. We receive compensation from lending partners when a successful match is made. This compensation does not influence the loan terms offered to you. All fees associated with any loan you accept are disclosed by the lender and are your responsibility.',
    },
    {
      title: '8. Prohibited Uses',
      text: 'You agree not to use the Service for any unlawful purpose or in violation of these Terms; to submit false, misleading, or fraudulent information; to impersonate any person or entity; to interfere with or disrupt the Service; to attempt to gain unauthorized access to any part of the Service; or to use the Service for any commercial purpose other than applying for financing.',
    },
    {
      title: '9. Intellectual Property',
      text: 'The Service and its original content, features, and functionality are owned by Lendo LLC and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works without our express written permission.',
    },
    {
      title: '10. Disclaimer of Warranties',
      text: 'The Service is provided "as is" and "as available" without any warranties of any kind, either express or implied. Lendo does not warrant that the Service will be uninterrupted, error-free, or free of viruses. We do not guarantee that you will receive any loan offers or that any offers will meet your needs.',
    },
    {
      title: '11. Limitation of Liability',
      text: 'To the maximum extent permitted by law, Lendo shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, arising from your use of the Service. Our total liability to you for any claims arising from these Terms shall not exceed $100.',
    },
    {
      title: '12. Indemnification',
      text: 'You agree to indemnify, defend, and hold harmless Lendo LLC and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys\' fees) arising from your use of the Service, violation of these Terms, or infringement of any third-party rights.',
    },
    {
      title: '13. Governing Law',
      text: 'These Terms shall be governed by and construed in accordance with the laws of the State of New York, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in New York County, New York.',
    },
    {
      title: '14. Changes to Terms',
      text: 'We reserve the right to modify these Terms at any time. We will notify you of material changes by email or by posting a notice on our platform. Your continued use of the Service after changes take effect constitutes your acceptance of the revised Terms.',
    },
    {
      title: '15. Contact Information',
      text: 'If you have questions about these Terms, please contact us at legal@lendo.com or write to: Lendo LLC, Legal Department, 123 Financial District, New York, NY 10004.',
    },
  ];

  return (
    <>
      <Navbar />
      <main style={{ paddingTop: '88px', paddingBottom: '80px', minHeight: '100vh', background: '#f7f9fc' }}>
        {/* Header */}
        <div style={{ background: '#1a2b4a', padding: '60px 24px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h1 style={{ fontFamily: 'Merriweather, serif', fontWeight: 900, fontSize: '2.2rem', color: '#ffffff', margin: '0 0 12px' }}>
              Terms & Conditions
            </h1>
            <p style={{ fontFamily: 'DM Sans, sans-serif', color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem', margin: 0 }}>
              Last updated: {lastUpdated}
            </p>
          </div>
        </div>

        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '48px 24px 0' }}>
          {/* Intro banner */}
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '20px 24px', marginBottom: '24px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>⚠️</span>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', color: '#92400e', lineHeight: 1.6, margin: 0 }}>
              <strong>Important:</strong> Please read these Terms and Conditions carefully before using Lendo. By using our service, you agree to be legally bound by these terms.
            </p>
          </div>

          {/* Sections */}
          {sections?.map((section, i) => (
            <div key={i} style={{ background: '#ffffff', borderRadius: '16px', padding: '28px 32px', marginBottom: '12px', border: '1px solid #e8edf3', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <h2 style={{ fontFamily: 'Merriweather, serif', fontWeight: 700, fontSize: '1.05rem', color: '#1a2b4a', margin: '0 0 12px' }}>
                {section?.title}
              </h2>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', color: '#4a5568', lineHeight: 1.8, margin: 0 }}>
                {section?.text}
              </p>
            </div>
          ))}

          {/* Financial disclaimer */}
          <div style={{ background: '#f0f4f8', borderRadius: '12px', padding: '24px', marginTop: '24px', border: '1px solid #d1dbe8' }}>
            <h3 style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: '0.9rem', color: '#2d3748', margin: '0 0 8px' }}>Financial Disclaimer</h3>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', color: '#718096', lineHeight: 1.7, margin: 0 }}>
              Lendo is not a lender, bank, or financial institution. We are a loan matching service. All loan products are provided by third-party lenders. Loan approval, terms, and rates are determined solely by the lender. APR ranges vary by lender and loan type. Please review all loan documents carefully before signing. Borrowing involves risk. Only borrow what you can afford to repay.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
